import { Suspense, lazy, useMemo, useState } from 'react';
import {
  AppLayout,
  Box,
  BreadcrumbGroup,
  Flashbar,
  SideNavigation,
  Spinner,
  TopNavigation,
  type AppLayoutProps,
  type FlashbarProps,
  type SideNavigationProps,
} from '@cloudscape-design/components';
import { Outlet, useLocation, useNavigate } from 'react-router';
import {
  useAuth,
  useRoles,
  useNotifyStore,
  usePreferences,
  AppNavContext,
  BreadcrumbsContext,
  type AppNavItem,
  type Breadcrumb,
} from '@deloitte-us-consulting/gp-sdk';
import wordmark from './assets/gp-wordmark.svg';
import { MoonIcon, SunIcon, UserIcon } from './assets/icons/ddsIcons';
// Plain constants + functions, no React and no markdown stack: importing this
// eagerly costs the entry chunk nothing and keeps the drawer itself lazy.
import { assistantDrawerSizing } from './chat/assistantWidth';
import { useRegisteredApps } from './remotes/useRegisteredApps';
import { isFrontDoorPath } from './frontDoorPaths';
import { isLinkedApp } from './remotes/registry';
import LinksPanel from './links/LinksPanel';
import { LINKS_COPY } from './links/growthPlatformsLinks';
import { LINKS_DRAWER_ID, isHomePath, useActiveDrawer } from './links/linksPanelState';
import { HeroPanelsContext, MENU_COPY } from './heroPanels';

/**
 * The assistant drawer's contents, loaded only when someone opens it.
 *
 * LAZY ON PURPOSE, and it must stay that way. `ChatPanel` reaches
 * `react-markdown` and `remark-gfm` through the SDK's `Markdown.tsx`, which
 * imports `remark-gfm` at module scope; a static import here would put the
 * whole markdown stack in the shell's entry chunk and charge every page load
 * for a panel most visits never open. Cloudscape renders an inactive drawer's
 * content not at all (`preserveInactiveContent` defaults to false), so the
 * dynamic import fires on first open and never before.
 */
const AssistantDrawer = lazy(() => import('./chat/AssistantDrawer'));

function toSideNavItems(items: AppNavItem[]): SideNavigationProps.Item[] {
  return items.map((i): SideNavigationProps.Item => {
    if (i.type === 'divider') return { type: 'divider' };
    if (i.type === 'section')
      return { type: 'section', text: i.text ?? '', items: toSideNavItems(i.items ?? []) };
    return { type: 'link', text: i.text ?? '', href: i.href ?? '#' };
  });
}

export default function App() {
  const { user, signOut } = useAuth();
  const { preferences, setPreferences } = usePreferences();
  const isDark = preferences.colorMode === 'dark';
  const { isPlatformAdmin, can } = useRoles();
  // Registry:Manage/Rbac:Manage (ADR 0010/0012's `admin` role) are both
  // org-wide, so appId doesn't matter — matches the '*' sentinel used
  // server-side (registry.ts/rbac.ts) and in the route gates (router.tsx).
  const canManageRegistry = isPlatformAdmin || can('Registry:Manage', { appId: '*' });
  const canManageRbac = isPlatformAdmin || can('Rbac:Manage', { appId: '*' });
  // The platform-level admin pages (ADR 0041). `can()` already ORs in the
  // gp-admin bypass, so these need no separate isPlatformAdmin term.
  const canManageAi = can('Ai:Manage', { appId: '*' });
  const canManageUsage = can('Usage:Manage', { appId: '*' });
  const canManageData = can('Data:Manage', { appId: '*' });
  const canManageEmail = can('Email:Manage', { appId: '*' });
  const navigate = useNavigate();
  const { pathname } = useLocation();
  // Registered apps sit alongside the taxonomy. Enabling an app in the registry is what
  // should make it reachable; until now it only made it reachable by URL.
  const { apps: registeredApps } = useRegisteredApps();

  // Which app the user is actually looking at. Registered remotes mount at
  // `/{appId}/*` (router.tsx), so the active app is resolved from the same
  // registry the nav is built from rather than from a second source of truth.
  //
  // The assistant is scoped to ONE app: the AI gateway authorizes per appId and
  // an app's tools come from its registry entry. So the assistant drawer is
  // offered where an app is mounted, and not on the dashboard or the admin
  // screens, where there would be nothing for it to be asked about.
  const activeAppId =
    registeredApps.find(
      (a) => pathname === `/${a.appId}` || pathname.startsWith(`/${a.appId}/`),
    )?.appId ?? null;

  const assistantDrawer: AppLayoutProps.Drawer | null =
    activeAppId === null
      ? null
      : {
          id: 'assistant',
          // Resizable on purpose: the assistant renders real tables, and a
          // fixed narrow panel makes a wide table unreadable.
          resizable: true,
          // `defaultSize` + `onResize`: opens at the width this user last
          // dragged to (or 720px, not the original 480 — see ./chat/
          // assistantWidth) and remembers every drag. AppLayout's own
          // `drawerSizes` state wins once the user resizes within a session, so
          // re-reading storage each render cannot fight an in-progress drag.
          ...assistantDrawerSizing(),
          ariaLabels: {
            drawerName: 'Assistant',
            triggerButton: 'Open assistant',
            closeButton: 'Close assistant',
            resizeHandle: 'Resize assistant',
          },
          trigger: { iconName: 'gen-ai' },
          content: (
            <Suspense
              fallback={
                <Box padding="l" textAlign="center">
                  <Spinner size="large" />
                </Box>
              }
            >
              <AssistantDrawer appId={activeAppId} />
            </Suspense>
          ),
        };

  // The GP Hub's right-hand links, on every shell page (ADR 0029). Static content,
  // so it's cheap to import eagerly, unlike the assistant.
  const linksDrawer: AppLayoutProps.Drawer = {
    id: LINKS_DRAWER_ID,
    ariaLabels: {
      drawerName: LINKS_COPY.drawerName,
      triggerButton: LINKS_COPY.triggerButton,
      closeButton: LINKS_COPY.closeButton,
    },
    // 'external' (not 'anchor-link', easy to mistake for an in-page jump link):
    // every tile here opens another site in a new tab, so the icon should say so.
    trigger: { iconName: 'external' },
    content: <LinksPanel />,
  };
  const offeredDrawers = assistantDrawer === null ? [linksDrawer] : [linksDrawer, assistantDrawer];
  // Controlled, because two drawers share one slot and home opens one from its own button.
  const { activeDrawerId, onDrawerChange } = useActiveDrawer(
    pathname,
    offeredDrawers.map((d) => d.id),
  );
  const linksOpen = activeDrawerId === LINKS_DRAWER_ID;
  // Cloudscape's drawer trigger is icon-only and is drawn for every drawer passed in,
  // even one without a `trigger`. On home the panel should read "Helpful External
  // Links", so the hero's HeroPanelButton opens it instead and the drawer is left out
  // until it's open (ADR 0044). Every other page keeps the small icon.
  const drawers =
    isHomePath(pathname) && !linksOpen ? offeredDrawers.filter((d) => d.id !== LINKS_DRAWER_ID) : offeredDrawers;
  const linksControl = {
    open: linksOpen,
    toggle: () => onDrawerChange({ detail: { activeDrawerId: linksOpen ? null : LINKS_DRAWER_ID } }),
  };

  // The side navigation starts closed on every page and then keeps the user's last
  // choice as they move around, since it's how they get between apps (ADR 0045). On
  // home, Cloudscape's icon-only toggle is hidden while it's closed and the hero's
  // "Menu" button opens it instead.
  const [navOpen, setNavOpen] = useState(false);
  const menuControl = { open: navOpen, toggle: () => setNavOpen(!navOpen) };

  // Shell-owned channels the mounted remote publishes into.
  const [appNav, setAppNav] = useState<AppNavItem[]>([]);
  const [crumbs, setCrumbs] = useState<Breadcrumb[]>([]);
  const appNavCtx = useMemo(() => ({ setItems: setAppNav }), []);
  const crumbsCtx = useMemo(() => ({ setItems: setCrumbs }), []);

  // Transient toasts (SDK store) → Cloudscape Flashbar.
  const notifications = useNotifyStore((s) => s.items);
  const dismiss = useNotifyStore((s) => s.dismiss);
  const flashItems: FlashbarProps.MessageDefinition[] = notifications.map((n) => ({
    id: n.id,
    type: n.type,
    header: n.header,
    content: n.content,
    dismissible: true,
    onDismiss: () => dismiss(n.id),
  }));

  // Administration links: each opens to gp-admin or the in-app `admin` role's
  // matching permission (ADR 0010/0012 decision 7a, ADR 0041) — same carve-out
  // as the API's requireAdminOrPermission and the router's RequirePermission
  // gate.
  const adminLinks: SideNavigationProps.Item[] = [
    ...(canManageAi
      ? ([{ type: 'link', text: 'AI settings', href: '/admin/ai' }] as SideNavigationProps.Item[])
      : []),
    ...(canManageUsage
      ? ([{ type: 'link', text: 'App Store usage', href: '/admin/usage' }] as SideNavigationProps.Item[])
      : []),
    ...(canManageData
      ? ([{ type: 'link', text: 'Data', href: '/admin/data' }] as SideNavigationProps.Item[])
      : []),
    ...(canManageEmail
      ? ([{ type: 'link', text: 'Email', href: '/admin/email' }] as SideNavigationProps.Item[])
      : []),
    ...(canManageRegistry
      ? ([
          { type: 'link', text: 'App Registry', href: '/admin/apps' },
          { type: 'link', text: 'App Onboarding', href: '/admin/onboarding' },
        ] as SideNavigationProps.Item[])
      : []),
    ...(canManageRbac ? ([{ type: 'link', text: 'RBAC', href: '/admin/rbac' }] as SideNavigationProps.Item[]) : []),
  ];

  // Platform taxonomy + the active remote's registered section nav.
  const navItems: SideNavigationProps.Item[] = [
    { type: 'link', text: 'Home', href: '/' },
    { type: 'link', text: 'About the AI Spine', href: '/about' },
    { type: 'divider' },
    // A linked app opens its externalUrl directly in a new tab (Cloudscape's
    // `external: true` sets target="_blank"/rel="noopener" and shows the
    // external-link cue); a hosted app routes to its shell path as usual. The
    // `onFollow` handler below already skips `preventDefault`/client routing
    // when `e.detail.external` is set, so this is the only change needed here.
    ...registeredApps.map((a): SideNavigationProps.Item => ({
      type: 'link',
      text: a.displayName,
      href: isLinkedApp(a) ? (a.externalUrl as string) : `/${a.appId}`,
      ...(isLinkedApp(a) ? { external: true } : {}),
    })),
    // Every authenticated user (not admin-gated) — submitting a request
    // needs no permission; the API decides per-request what a given user
    // can approve (ADR 0010).
    { type: 'link', text: 'Access Requests', href: '/access-requests' },
    { type: 'divider' },
    // Onboarding: also every authenticated user, same precedent as Access
    // Requests above — orientation and submission need no permission; the
    // API-side Registry:Manage gate only kicks in for the admin review queue.
    {
      type: 'section',
      text: 'Onboard a New Application',
      items: [
        { type: 'link', text: 'Get started', href: '/onboard' },
        { type: 'link', text: 'Submit an app', href: '/onboard/submit' },
        { type: 'link', text: 'My submissions', href: '/onboard/mine' },
      ],
    },
    ...(appNav.length ? [{ type: 'divider' as const }, ...toSideNavItems(appNav)] : []),
    ...(adminLinks.length
      ? ([
          { type: 'divider' },
          { type: 'section', text: 'Administration', items: adminLinks },
        ] as SideNavigationProps.Item[])
      : []),
  ];

  return (
    <AppNavContext.Provider value={appNavCtx}>
      <BreadcrumbsContext.Provider value={crumbsCtx}>
        <TopNavigation
          identity={{ href: '/', title: '', logo: { src: wordmark, alt: 'Growth Platforms App Store' } }}
          utilities={[
            {
              type: 'button',
              ariaLabel: isDark ? 'Switch to light mode' : 'Switch to dark mode',
              title: isDark ? 'Switch to light mode' : 'Switch to dark mode',
              iconSvg: isDark ? <SunIcon /> : <MoonIcon />,
              disableUtilityCollapse: true,
              onClick: () => setPreferences({ colorMode: isDark ? 'light' : 'dark' }),
            },
            {
              type: 'menu-dropdown',
              text: user?.email ?? 'Account',
              iconSvg: <UserIcon />,
              items: [{ id: 'signout', text: 'Sign out' }],
              onItemClick: (e) => {
                if (e.detail.id === 'signout') signOut();
              },
            },
          ]}
        />
        <AppLayout
          toolsHide
          navigationOpen={navOpen}
          onNavigationChange={({ detail }) => setNavOpen(detail.open)}
          navigationHide={isHomePath(pathname) && !navOpen}
          ariaLabels={{
            navigation: MENU_COPY.region,
            navigationToggle: MENU_COPY.toggle,
            navigationClose: MENU_COPY.close,
          }}
          {...(isFrontDoorPath(pathname)
            ? { disableContentPaddings: true, maxContentWidth: Number.MAX_VALUE }
            : {})}
          navigation={
            <SideNavigation
              activeHref={window.location.pathname}
              items={navItems}
              onFollow={(e) => {
                if (!e.detail.external) {
                  e.preventDefault();
                  navigate(e.detail.href);
                }
              }}
            />
          }
          breadcrumbs={
            crumbs.length ? (
              <BreadcrumbGroup
                items={crumbs}
                onFollow={(e) => {
                  if (e.detail.href !== '#') {
                    e.preventDefault();
                    navigate(e.detail.href);
                  }
                }}
              />
            ) : undefined
          }
          notifications={<Flashbar items={flashItems} stackItems />}
          content={
            <HeroPanelsContext.Provider value={{ links: linksControl, menu: menuControl }}>
              <Outlet />
            </HeroPanelsContext.Provider>
          }
          drawers={drawers}
          activeDrawerId={activeDrawerId}
          onDrawerChange={onDrawerChange}
        />
      </BreadcrumbsContext.Provider>
    </AppNavContext.Provider>
  );
}
